import { useState, type ReactNode, type CSSProperties } from 'react';
import styles from './ListPage.module.css';
import { cx } from '../../lib/cx';

export type ListStatus = 'draft' | 'done' | 'neutral';

export interface ListPageProps {
  /**
   * Hlavní nadpis stránky.
   */
  title: ReactNode;
  /**
   * Kořen navigace nad titulem. Může být jednoduchý řetězec ("BOZP · Úrazy"),
   * nebo pole segmentů pro breadcrumb ve stylu DocLayout (["Realizace","Vady"]).
   * @deprecated použij `breadcrumb` pro jednotný look s dokumenty.
   */
  eyebrow?: ReactNode;
  breadcrumb?: ReactNode[];
  subtitle?: ReactNode;
  /** Pill napravo v hlavičce (matches DocLayout statusBadge). */
  status?: ListStatus;
  statusText?: ReactNode;
  actions?: ReactNode;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  tools?: ReactNode;
  children: ReactNode;
}

/**
 * Sdílený layout pro list stránky (Vady, RFI, KD, Úkoly, Subdodavatelé, Archiv…).
 * Vizuál sjednocený s `DocLayout` — stejný dark header s breadcrumbem,
 * titulem, status pillem a actions, a světlý canvas pod ním.
 */
export function ListPage({
  title,
  eyebrow,
  breadcrumb,
  subtitle,
  status,
  statusText,
  actions,
  searchPlaceholder,
  onSearchChange,
  tools,
  children,
}: ListPageProps) {
  const [q, setQ] = useState('');

  // Breadcrumb priorita: explicit array > eyebrow (rozdělený " · " nebo "/")
  const crumbs: ReactNode[] =
    breadcrumb ??
    (typeof eyebrow === 'string'
      ? eyebrow.split(/\s*[·/]\s*/).filter(Boolean)
      : eyebrow != null
        ? [eyebrow]
        : []);

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          {crumbs.length > 0 && (
            <div className={styles.breadcrumb}>
              {crumbs.flatMap((c, i) =>
                i === 0
                  ? [<span key={i}>{c}</span>]
                  : [
                      <span key={`s${i}`} className={styles.sep}>/</span>,
                      <span key={i}>{c}</span>,
                    ],
              )}
            </div>
          )}
          <h1 className={styles.title}>{title}</h1>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>

        {status && statusText && (
          <div
            className={cx(
              styles.statusBadge,
              status === 'done' && styles.statusDone,
              status === 'neutral' && styles.statusNeutral,
            )}
          >
            <span className={styles.statusDot} aria-hidden />
            {statusText}
          </div>
        )}

        {actions && <div className={styles.headerActions}>{actions}</div>}
      </header>

      <div className={styles.body}>
        <div className={styles.bodyInner}>
          {(searchPlaceholder !== undefined || tools) && (
            <div className={styles.toolbar}>
              {searchPlaceholder !== undefined && (
                <div className={styles.searchWrap}>
                  <span className={styles.searchIcon} aria-hidden>
                    ⌕
                  </span>
                  <input
                    type="search"
                    placeholder={searchPlaceholder}
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      onSearchChange?.(e.target.value);
                    }}
                  />
                </div>
              )}
              {tools}
            </div>
          )}

          <div className={styles.content}>{children}</div>
        </div>
      </div>
    </div>
  );
}

export interface ListColumn<T> {
  key: string;
  label: ReactNode;
  width?: string;
  align?: 'left' | 'right';
  mono?: boolean;
  dim?: boolean;
  primary?: boolean;
  render?: (row: T) => ReactNode;
}

export interface ListTableProps<T> {
  columns: ListColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  emptyTitle?: ReactNode;
  emptyText?: ReactNode;
  /**
   * Tabulka se vejde do šířky kontejneru bez vodorovného posuvníku — pružné
   * sloupce se smrští a obsah se ořízne (…), místo aby tabulku roztáhly.
   * Vhodné pro listy s jedním řádkem na záznam (např. Moje stavby).
   */
  fluid?: boolean;
  /**
   * Zapne úvodní sloupec se zaškrtávátky pro hromadný výběr řádků.
   * Výběr řídí volající přes `selectedKeys` + `onToggleRow`/`onToggleAll`.
   */
  selectable?: boolean;
  selectedKeys?: Set<string | number>;
  onToggleRow?: (row: T) => void;
  onToggleAll?: () => void;
  /** Akce na konci řádku (ikony stáhnout/sdílet/smazat). */
  rowActions?: (row: T) => ReactNode;
  actionsWidth?: string;
}

export function ListTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyTitle,
  emptyText,
  fluid,
  selectable,
  selectedKeys,
  onToggleRow,
  onToggleAll,
  rowActions,
  actionsWidth = '120px',
}: ListTableProps<T>) {
  const flexTrack = fluid ? 'minmax(0, 1fr)' : 'minmax(220px, 1fr)';
  const gridTemplate = [
    selectable ? '40px' : null,
    ...columns.map((c) => c.width ?? flexTrack),
    rowActions ? actionsWidth : null,
  ]
    .filter(Boolean)
    .join(' ');
  const gridStyle: CSSProperties = { gridTemplateColumns: gridTemplate };

  // Robustní fallback — `rows` by mělo být pole, ale kdyby přišlo undefined
  // (rozběhlý refetch / chyba typu), chceme viditelný empty state místo pádu.
  const safeRows = Array.isArray(rows) ? rows : [];

  if (safeRows.length === 0) {
    return (
      <div className={styles.emptyState}>
        <h3>{emptyTitle ?? 'Zatím žádné záznamy'}</h3>
        <div>{emptyText ?? 'Nový záznam přidáte tlačítkem v pravém horním rohu.'}</div>
      </div>
    );
  }

  const allSelected = !!selectedKeys && safeRows.length > 0 && safeRows.every((r) => selectedKeys.has(rowKey(r)));

  return (
    <div className={cx(styles.table, fluid && styles.tableFluid)} role="table">
      <div className={styles.tableInner}>
        <div className={styles.tableHead} style={gridStyle} role="row">
          {selectable && (
            <div role="columnheader" style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => onToggleAll?.()}
                aria-label="Vybrat vše"
                style={{ cursor: 'pointer', width: 16, height: 16 }}
              />
            </div>
          )}
          {columns.map((c) => (
            <div
              key={c.key}
              className={cx(c.align === 'right' && styles.right)}
              style={c.align === 'right' ? { textAlign: 'right' } : undefined}
              role="columnheader"
            >
              {c.label}
            </div>
          ))}
          {rowActions && <div role="columnheader" />}
        </div>
        {safeRows.map((row) => {
          const key = rowKey(row);
          const checked = !!selectedKeys?.has(key);
          return (
            <div
              key={key}
              className={styles.tableRow}
              style={gridStyle}
              role="row"
              onClick={() => onRowClick?.(row)}
            >
              {selectable && (
                <div
                  className={styles.cell}
                  role="cell"
                  style={{ display: 'flex', alignItems: 'center' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleRow?.(row)}
                    aria-label="Vybrat řádek"
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                </div>
              )}
              {columns.map((c) => {
                const value = c.render ? c.render(row) : (row as Record<string, ReactNode>)[c.key];
                return (
                  <div
                    key={c.key}
                    // data-label nese popisek sloupce — na mobilu se tabulka
                    // stohuje do karet a popisek se vykreslí přes ::before.
                    data-label={typeof c.label === 'string' ? c.label : undefined}
                    className={cx(
                      styles.cell,
                      c.mono && styles.mono,
                      c.dim && styles.dim,
                      c.primary && styles.cellPrimary,
                      c.align === 'right' && styles.right,
                    )}
                    role="cell"
                  >
                    {value}
                  </div>
                );
              })}
              {rowActions && (
                <div
                  className={styles.cell}
                  role="cell"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {rowActions(row)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
